import { createAction } from '@reduxjs/toolkit';

import type { AssetContainerModel, AssetContainerTypes, LicenseEnum } from 'js/src/client';
import type {
    SearchContextValueType,
    StoredAssetContainer,
    StoredMediaResult,
} from 'js/src/data/media/media-types';
import type {
    CompletedFileAsset,
    FileMetadata,
    FileWithMetadata,
    UploadOptions,
} from 'js/src/data/uploads/uploads-types';
import { UPLOAD_ACTIONS } from 'js/src/data/uploads/uploads-types';
import type { DeepReadonly } from 'js/src/libs/consts';
import type { AugmentedFile } from 'js/src/libs/native-file-handler';

export const removeUpload = createAction<{
    asset: StoredAssetContainer | StoredMediaResult;
    search_context: SearchContextValueType;
    activeFolderId: number | null;
}>(UPLOAD_ACTIONS.REMOVE_UPLOAD);

export const clearUploads = createAction(UPLOAD_ACTIONS.CLEAR_UPLOADS);

export const finalizeAttributionData = createAction<{
    uploadSessionId: string;
    attribution: string;
    license: LicenseEnum;
}>(UPLOAD_ACTIONS.FINALIZE_ATTRIBUTION_DATA);

export const updateUploadMediaTags = createAction<{
    uploadSessionId: string;
    mediaTags: string[];
}>(UPLOAD_ACTIONS.UPDATE_UPLOAD_MEDIA_TAGS);

export const updateAttributionData = createAction<{
    uploadSessionId: string;
    attribution: string;
}>(UPLOAD_ACTIONS.UPDATE_ATTRIBUTION_DATA);

export const startUploadExperience = createAction<{
    uploadSessionId: string;
}>(UPLOAD_ACTIONS.START_UPLOAD_EXPERIENCE);

export const endUploadExperience = createAction<{
    uploadSessionId: string;
    folderId: number | null;
}>(UPLOAD_ACTIONS.END_UPLOAD_EXPERIENCE);

export const setUploadFolder = createAction<{
    uploadSessionId: string | undefined;
    folderId: number | null;
}>(UPLOAD_ACTIONS.SET_UPLOAD_FOLDER);

export const cancelAllUploads = createAction<{
    uploadSessionId: string;
}>(UPLOAD_ACTIONS.CANCEL_ALL_UPLOADS);

export const userCancelFile = createAction<{
    uploadSessionId: string;
    file_obj: AugmentedFile;
}>(UPLOAD_ACTIONS.USER_CANCEL_FILE);

export const addFiles = createAction<{
    uploadSessionId: string;
    opts: UploadOptions;
}>(UPLOAD_ACTIONS.ADD_FILES);

export const storeUploadProgress = createAction<{
    progress: number;
    session_id: string;
    file_progress?: Readonly<Record<string, number>>;
}>(UPLOAD_ACTIONS.STORE_UPLOAD_PROGRESS);

export const storeSuccessfulUpload = createAction<{
    uploadSessionId: string;
    file_obj: AugmentedFile;
    new_asset: DeepReadonly<AssetContainerModel>;
    new_asset_type: AssetContainerTypes;
    async_operation_uuid: string;
}>(UPLOAD_ACTIONS.STORE_SUCCESSFUL_UPLOAD);

export const storeFailedUpload = createAction<{
    uploadSessionId: string;
    file_obj: AugmentedFile;
    message: string;
}>(UPLOAD_ACTIONS.STORE_FAILED_UPLOAD);

export const clearFailedUploadMessages = createAction(UPLOAD_ACTIONS.CLEAR_FAILED_UPLOAD_MESSAGES);

export const updateUploadedAssetMediaTags = createAction<{
    uploadSessionId: string;
    file_obj: AugmentedFile;
    tags: string[];
}>(UPLOAD_ACTIONS.UPDATE_UPLOADED_ASSET_MEDIA_TAGS);

export const markFileAsFinalizing = createAction<{
    uploadSessionId: string;
    file_id: string;
}>(UPLOAD_ACTIONS.MARK_FILE_AS_FINALIZING);

export const markFileAsDeleted = createAction<{
    uploadSessionId: string;
    file_id: string;
}>(UPLOAD_ACTIONS.MARK_FILE_AS_DELETED);

export const markFileAsDeleting = createAction<{
    uploadSessionId: string;
    file_id: string;
}>(UPLOAD_ACTIONS.MARK_FILE_AS_DELETING);

export const markFileAsDeletionFailed = createAction<{
    uploadSessionId: string;
    file_id: string;
}>(UPLOAD_ACTIONS.MARK_FILE_AS_DELETION_FAILED);

export const storeCompletedFile = createAction<{
    uploadSessionId: string;
    file_id: string;
    new_asset: CompletedFileAsset;
    new_asset_type: AssetContainerTypes;
}>(UPLOAD_ACTIONS.STORE_COMPLETED_FILE);

export const storeUpdatedFileMetadata = createAction<{
    uploadSessionId: string;
    file_with_metadata: FileWithMetadata;
    new_metadata: FileMetadata;
}>(UPLOAD_ACTIONS.STORE_UPDATED_FILE_METADATA);

export const createUploadSession = createAction<{
    uploadSessionId: string;
}>(UPLOAD_ACTIONS.CREATE_UPLOAD_SESSION);

export const deleteUploadSession = createAction<{
    uploadSessionId: string;
}>(UPLOAD_ACTIONS.DELETE_UPLOAD_SESSION);
